import { claimIfDue, type OfflineSummary } from '../backend/claim';
import { carryFarmQueue, enqueueFarmClear, type FarmQueue } from '../backend/farmQueue';
import { queueFeedback } from '../backend/feedbackQueue';
import { feedbackDraft } from '../backend/feedbackReport';
import type { AccountState, BackendEvent, FrontierRequest, GearRequest, SaveBackend } from '../backend/localBackend';
import { RemoteError } from '../backend/remoteApi';
import { getStage, LAST_STAGE_ID } from '../data/stages';
import { MAX_FARM_STAGE } from '../sim/rateCaps';
import { createNewFile } from '../model/save';
import {
  RULE_SLOTS,
  SLOT_IDS,
  SLOT_MAX_RANK,
  type Condition,
  type GearSlot,
  type Rarity,
  type Rule,
  type SaveData,
  type SaveFile,
  type SlotId,
  type SubSpecId,
  type TargetKind,
} from '../model/types';
import { benchmarkRates } from '../sim/benchmark';
import { awardedClearXp, grantXp, resolveHero, type ResolvedHero } from '../sim/build';
import { createCombat, finalizeCombat, stepCombat, type CombatResult, type CombatState } from '../sim/combat';
import { rollFarmDrops } from '../sim/drops';
import { absorbDrops, previewRecycle } from '../sim/gear';
import { FARM_BATCH_INTERVAL_MS, isRepeatFarmStage } from '../server/farmBatch';
import { FUNCTION_ERROR_MESSAGES } from '../server/functionErrors';
import { applyGearAction } from '../server/gearAction';
import { readBagView, writeBagView, type BagFilter, type BagSort } from './bagView';
import { earnedOfflineCapHours } from '../sim/offline';
import { applyFightResult, cancelDrill, resumePush, stageToFight, startDrill } from '../sim/progress';
import { mixSeed } from '../sim/rng';
import { CLEAR_AUTO_RETRIES, clearRetryWaitMs, FARM_TURNAROUND_MS, rebraidRanks, seedsForFight, setSpec as commitSpec, spendRanks } from '../server/simAuthority';
import { recentClientErrors } from './clientErrors';
import { gameVersion } from './gameVersion';
import { captureStageJpeg } from './screenshot';

export type TabId = 'combat' | 'hero' | 'build';
export type WatchSpeed = 1 | 2;

/** Quiet gap that folds rapid spends into one spend_points call. */
export const SPEND_BATCH_MS = 400;
export const SPEND_REJECTED = 'That spend did not land. Points were put back.';

/** Pause after a fight before the next one starts, at both watch speeds. */
const CLIENT_FIGHT_GAP_MS = 800;

type Listener = (scope: 'all' | 'status') => void;

export class GameController {
  readonly backend: SaveBackend;
  file: SaveFile;
  fight: CombatState | null = null;
  tab: TabId = 'combat';
  rulesOpen = false;
  rulesFocus: 'back' | 'open' | null = null;
  private rulesPushed = false;
  watchSpeed: WatchSpeed = 1;
  modal: OfflineSummary | null = null;
  lastLog: string[] = [];
  lastResult: CombatResult | null = null;
  account: AccountState;
  feedbackOpen = false;
  feedbackKind: 'bug' | 'idea' = 'bug';
  feedbackMessage = '';
  feedbackShot = true;
  feedbackNotice: string | null = null;
  feedbackSending = false;
  bagFilter: BagFilter;
  bagSort: BagSort;
  recycleBelow: Rarity = 'rare';
  recycleToast: string | null = null;
  private toastTimer = 0;
  /** Save from before the current unconfirmed spend streak. */
  private spendBase: SaveData | null = null;
  private spendPending = new Map<SlotId, number>();
  /** Ranks sent in the current spend_points call and not yet answered. */
  private spendInFlight = new Map<SlotId, number>();
  /** Save from before the ranks that are on the wire. */
  private spendFlightBase: SaveData | null = null;
  private spendTimer: ReturnType<typeof setTimeout> | null = null;
  private spendFlight: Promise<void> | null = null;
  /** Quiet pause while a clear waits out the server gap. Not an error. */
  breathing = false;
  /** Stage kept after the auto-retry cap. A tap submits it again. A new fight does not replace it. */
  queuedClearStage: number | null = null;
  /** Repeat-farm wins waiting for one validate_clear. The fight loop does not wait on this. */
  private farmQueue: FarmQueue | null = null;
  private farmSending = false;
  private farmFlush: Promise<void> = Promise.resolve();
  /** A different stage won while a batch was in flight. Queued after that batch returns. */
  private pendingFarmStage: number | null = null;
  readonly ready: Promise<void>;
  private nextFightAt = 0;
  private dropSeed = 1;
  private serverBusy = false;
  /** Notice last set by a failed guest save. A later success clears only this text. */
  private guestSaveFault: string | null = null;
  private listeners = new Set<Listener>();
  private readyResolve: () => void = () => undefined;

  constructor(backend: SaveBackend) {
    this.backend = backend;
    this.file = createNewFile();
    const bagView = readBagView();
    this.bagFilter = bagView.filter;
    this.bagSort = bagView.sort;
    this.account = backend.account();
    this.ready = new Promise((resolve) => {
      this.readyResolve = resolve;
    });
    this.backend.subscribe?.((event) => this.onBackend(event));
  }

  subscribe(listener: Listener): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  boot(): void {
    const pending = this.backend.prepare?.();
    if (pending) {
      void pending.then(
        () => this.finishBoot(),
        () => this.finishBoot(),
      );
      return;
    }
    this.finishBoot();
  }

  setTab(tab: TabId): void {
    if (this.rulesOpen) {
      this.closeRules();
      if (tab === 'build') return;
    }
    if (this.tab === 'build' && tab !== 'build') void this.flushSpend();
    this.tab = tab;
    this.emit();
  }

  openRules(): void {
    if (this.rulesOpen) return;
    void this.flushSpend();
    this.tab = 'build';
    this.rulesOpen = true;
    this.rulesFocus = 'back';
    if (typeof history !== 'undefined') {
      history.pushState({ threadboundRules: true }, '');
      this.rulesPushed = true;
    }
    this.emit();
  }

  closeRules(fromHistory = false): void {
    if (!this.rulesOpen) return;
    this.rulesOpen = false;
    this.rulesFocus = 'open';
    const shouldBack = !fromHistory && this.rulesPushed;
    this.rulesPushed = false;
    this.emit();
    if (shouldBack) history.back();
  }

  setWatchSpeed(speed: WatchSpeed): void {
    this.watchSpeed = speed;
    this.emit();
  }

  readyForNext(now: number): boolean {
    return this.fight === null && !this.serverBusy && this.queuedClearStage === null && now >= this.nextFightAt;
  }

  startFight(): void {
    if (this.fight || this.serverBusy || this.queuedClearStage !== null || this.account.readOnly) return;
    const data = this.file.data;
    const stage = getStage(stageToFight(data.progress));
    const seeds = seedsForFight(data.idle.fightSerial, stage.id);
    this.dropSeed = seeds.drops;
    if (this.backend.offlineClaims !== 'server') {
      data.idle.fightSerial = seeds.nextSerial;
      this.persist();
    }
    this.fight = createCombat({ hero: this.resolved(), stage, seed: seeds.combat });
  }

  stepFight(): void {
    if (!this.fight || this.fight.finished) return;
    this.fight = stepCombat(this.fight);
    this.patchLog();
    if (!this.fight.finished) return;
    if (this.account.readOnly) {
      this.fight = null;
      return;
    }
    const result = finalizeCombat(this.fight);
    this.fight = null;
    const paceFarm =
      result.outcome === 'victory' &&
      this.backend.offlineClaims === 'server' &&
      Boolean(this.backend.settleFarmBatch) &&
      isRepeatFarmStage(this.file.data.progress, result.stageId);
    this.nextFightAt = performance.now() + CLIENT_FIGHT_GAP_MS + (paceFarm ? FARM_TURNAROUND_MS : 0);
    this.applyFight(result);
  }

  hide(): void {
    if (this.account.readOnly) return;
    if (this.backend.offlineClaims === 'server') {
      // Start both in this turn. keepalive lets the spend finish if the tab dies first.
      // The snapshot waits for that response so unconfirmed ranks are not written.
      const spending = this.flushSpend({ keepalive: true });
      const farming = this.flushFarm({ keepalive: true });
      void spending.then(() => {
        if (this.account.readOnly) return;
        this.persistConfirmedSpend();
      });
      void Promise.allSettled([spending, farming]).then(() => {
        void this.backend.flush?.().then(() => this.backend.markAway?.());
      });
      return;
    }
    this.refreshSnapshot();
    this.file.data.idle.lastActiveAtMs = this.backend.serverNow();
    this.file.data.idle.pendingClaim = true;
    this.persist();
  }

  show(): void {
    if (this.backend.offlineClaims === 'server') {
      void this.backend.markBack?.().then((summary) => {
        const loaded = this.backend.load();
        if (loaded) this.refreshAccount(this.adoptServerFile(loaded));
        else this.account = this.backend.account();
        if (summary) this.modal = summary;
        this.emit();
      }).catch(() => {
        this.account = { ...this.account, notice: 'Could not resume the cloud session.' };
        this.emit();
      });
      return;
    }
    this.claimReturn(false);
    this.emit();
  }

  previewAway(ms: number): void {
    if (this.backend.offlineClaims === 'server' || this.account.readOnly) return;
    this.refreshSnapshot();
    this.file.data.idle.lastActiveAtMs = this.backend.serverNow();
    this.file.data.idle.pendingClaim = true;
    this.persist();
    this.backend.advanceClock(ms);
    this.claimReturn(true);
    this.emit();
  }

  dismissModal(): void {
    this.modal = null;
    this.emit();
  }

  resume(): void {
    if (this.account.readOnly) return;
    if (this.backend.offlineClaims === 'server') {
      void this.adoptFrontier({ action: 'push' });
      return;
    }
    this.file.data.progress = resumePush(cancelDrill(this.file.data.progress));
    this.restartFight();
    this.persist();
    this.emit();
  }

  drill(): void {
    if (this.account.readOnly) return;
    const candidate = this.drillCandidate();
    if (candidate === null) return;
    if (this.backend.offlineClaims === 'server') {
      void this.adoptFrontier({ action: 'drill', stageId: candidate });
      return;
    }
    this.file.data.progress = startDrill(this.file.data.progress, candidate, false);
    this.restartFight();
    this.persist();
    this.emit();
  }

  stopDrill(): void {
    if (this.account.readOnly) return;
    if (this.backend.offlineClaims === 'server') {
      void this.adoptFrontier({ action: 'cancel_drill' });
      return;
    }
    this.file.data.progress = cancelDrill(this.file.data.progress);
    this.restartFight();
    this.persist();
    this.emit();
  }

  drillCandidate(): number | null {
    const progress = this.file.data.progress;
    for (let id = Math.min(progress.bestCleared, MAX_FARM_STAGE); id >= 1; id -= 1) {
      const stage = getStage(id);
      if (!stage.isWall && id > progress.farmStage) return id;
    }
    return null;
  }

  spendPoint(slot: SlotId): void {
    this.spendPoints(slot, 1);
  }

  spendPoints(slot: SlotId, raw: number | 'max'): void {
    if (this.account.readOnly) return;
    const count = this.pointsToSpend(slot, raw);
    if (count < 1) return;
    if (this.backend.offlineClaims !== 'server') {
      try {
        this.file.data = spendRanks(this.file.data, [{ slot, count }]);
      } catch (error) {
        this.failGuest(error);
        return;
      }
      this.clearSpendRejection();
      this.afterBuildChange();
      return;
    }
    const base = this.spendBase ?? structuredClone(this.file.data);
    let next: SaveData;
    try {
      next = spendRanks(this.file.data, [{ slot, count }]);
    } catch {
      return;
    }
    this.spendBase = base;
    this.file.data = next;
    this.spendPending.set(slot, (this.spendPending.get(slot) ?? 0) + count);
    this.armSpendTimer();
    this.clearSpendRejection();
    this.emit();
  }

  /** Sends the queued spend now. Leaving Build, hiding the tab, or changing specs calls this. */
  flushSpend(options?: { keepalive?: boolean }): Promise<void> {
    if (this.spendTimer !== null) {
      clearTimeout(this.spendTimer);
      this.spendTimer = null;
    }
    const flight = this.spendFlight;
    if (flight) return flight.then(() => this.flushSpend(options));
    if (this.spendPending.size === 0) return Promise.resolve();
    const ranks = SLOT_IDS.flatMap((slot) => {
      const count = this.spendPending.get(slot) ?? 0;
      return count > 0 ? [{ slot, count }] : [];
    });
    const base = structuredClone(this.spendBase ?? this.file.data);
    this.spendPending.clear();
    this.spendBase = null;
    this.spendInFlight = new Map(ranks.map(({ slot, count }) => [slot, count]));
    this.spendFlightBase = base;
    const flightPromise = this.sendSpend(ranks, base, options);
    let wrapped: Promise<void>;
    wrapped = flightPromise.finally(() => {
      if (this.spendFlight === wrapped) this.spendFlight = null;
    });
    this.spendFlight = wrapped;
    return wrapped;
  }

  setSpec(slot: SlotId, spec: SubSpecId | null): void {
    if (this.account.readOnly) return;
    this.afterSpendSettled(() => {
      if (this.account.readOnly) return;
      if (this.backend.offlineClaims === 'server') {
        void this.adoptServer(() => this.backend.applySpend?.({ action: 'set_spec', slot, spec }));
        return;
      }
      try {
        this.file.data = commitSpec(this.file.data, slot, spec);
      } catch (error) {
        this.failGuest(error);
        return;
      }
      this.afterBuildChange();
    });
  }

  rebraid(): void {
    if (this.account.readOnly) return;
    this.afterSpendSettled(() => {
      if (this.account.readOnly) return;
      if (this.backend.offlineClaims === 'server') {
        void this.adoptServer(() => this.backend.applySpend?.({ action: 'rebraid' }));
        return;
      }
      try {
        this.file.data = rebraidRanks(this.file.data);
      } catch (error) {
        this.failGuest(error);
        return;
      }
      this.afterBuildChange();
    });
  }

  addRule(): void {
    if (this.account.readOnly) return;
    const data = this.file.data;
    if (data.hero.rules.length >= RULE_SLOTS) return;
    const abilityId = this.resolved().abilities[0]?.id;
    if (!abilityId) return;
    data.hero.rules.push({
      id: nextRuleId(data.hero.rules),
      condition: { kind: 'always' },
      abilityId,
      target: 'lowestHp',
    });
    this.afterBuildChange();
  }

  removeRule(index: number): void {
    if (this.account.readOnly) return;
    this.file.data.hero.rules.splice(index, 1);
    this.afterBuildChange();
  }

  moveRule(index: number, direction: -1 | 1): void {
    if (this.account.readOnly) return;
    const rules = this.file.data.hero.rules;
    const next = index + direction;
    if (next < 0 || next >= rules.length) return;
    const [rule] = rules.splice(index, 1);
    if (!rule) return;
    rules.splice(next, 0, rule);
    this.afterBuildChange();
  }

  setRuleAbility(index: number, abilityId: string): void {
    if (this.account.readOnly) return;
    const rule = this.file.data.hero.rules[index];
    if (!rule) return;
    if (!this.resolved().abilities.some((ability) => ability.id === abilityId)) return;
    rule.abilityId = abilityId;
    this.afterBuildChange();
  }

  setRuleTarget(index: number, target: TargetKind): void {
    if (this.account.readOnly) return;
    const rule = this.file.data.hero.rules[index];
    if (!rule) return;
    rule.target = target;
    this.afterBuildChange();
  }

  setRuleCondition(index: number, kind: Condition['kind'], param?: number): void {
    if (this.account.readOnly) return;
    const rule = this.file.data.hero.rules[index];
    if (!rule) return;
    rule.condition = conditionFrom(kind, param, rule.condition);
    this.afterBuildChange();
  }

  setBagFilter(filter: BagFilter): void {
    this.bagFilter = filter;
    writeBagView({ filter, sort: this.bagSort });
    this.emit();
  }

  toggleBagSort(): void {
    this.bagSort = this.bagSort === 'high' ? 'low' : 'high';
    writeBagView({ filter: this.bagFilter, sort: this.bagSort });
    this.emit();
  }

  setRecycleBelow(rarity: Rarity): void {
    this.recycleBelow = rarity;
    this.emit();
  }

  equip(itemId: string): void {
    this.dispatchGear({ action: 'equip', itemId }, true);
  }

  unequip(slot: GearSlot): void {
    this.dispatchGear({ action: 'unequip', slot }, true);
  }

  salvage(itemId: string): void {
    this.dispatchGear({ action: 'salvage', itemId }, false);
  }

  enhance(slot: GearSlot): void {
    this.dispatchGear({ action: 'enhance', slot }, true);
  }

  lockItem(itemId: string): void {
    this.dispatchGear({ action: 'lock', itemId }, false);
  }

  unlockItem(itemId: string): void {
    this.dispatchGear({ action: 'unlock', itemId }, false);
  }

  setAutoRecycle(rarity: Rarity | null): void {
    this.dispatchGear({ action: 'set_auto_recycle', rarity }, false);
  }

  recycleBulk(): void {
    if (this.account.readOnly) return;
    const preview = previewRecycle(this.file.data, this.recycleBelow);
    if (preview.count < 1) return;
    const reward = rewardLabel(preview);
    const line = this.recycleBelow.charAt(0).toUpperCase() + this.recycleBelow.slice(1);
    if (typeof window !== 'undefined' && !window.confirm(`Recycle ${preview.count} unlocked items below ${line}? You get ${reward} back.`)) {
      return;
    }
    this.dispatchGear({ action: 'recycle_bulk', rarity: this.recycleBelow }, false, `Recycled ${preview.count} items (${reward})`);
  }

  signInWithGoogle(): Promise<void> {
    return this.backend.signInWithGoogle?.() ?? Promise.resolve();
  }

  keepGoogleSave(): Promise<void> {
    return this.backend.keepGoogleSave?.() ?? Promise.resolve();
  }

  stayGuest(): void {
    this.backend.stayGuest?.();
    this.account = this.backend.account();
    this.emit();
  }

  openFeedback(): void {
    this.feedbackOpen = true;
    this.feedbackNotice = null;
    this.emit();
  }

  closeFeedback(): void {
    this.feedbackOpen = false;
    this.feedbackNotice = null;
    this.emit();
  }

  async submitFeedback(): Promise<void> {
    if (this.feedbackSending) return;
    const message = this.feedbackMessage.trim();
    if (message.length < 1) {
      this.feedbackNotice = 'Write a short note first.';
      this.emit();
      return;
    }
    this.feedbackSending = true;
    this.feedbackNotice = null;
    this.emit();
    const cloud = this.account.mode === 'cloud' && Boolean(this.backend.submitFeedback);
    const draft = feedbackDraft({
      kind: this.feedbackKind,
      message,
      gameVersion: gameVersion(),
      userAgent: typeof navigator === 'undefined' ? '' : navigator.userAgent,
      stage: stageToFight(this.file.data.progress),
      errors: recentClientErrors(),
      saveVersion: this.file.version,
      url: typeof location === 'undefined' ? '' : location.href,
      screenshotDataUrl: cloud && this.feedbackShot ? captureStageJpeg() : null,
    });
    try {
      if (cloud && this.backend.submitFeedback) {
        await this.backend.submitFeedback(draft);
        this.feedbackMessage = '';
        this.feedbackNotice = 'Report sent.';
      } else {
        queueFeedback(draft);
        this.feedbackMessage = '';
        this.feedbackNotice = 'Saved on this browser. It sends when a cloud save is signed in.';
      }
    } catch (error) {
      const detail = error instanceof Error ? error.message : '';
      this.feedbackNotice = detail.includes('feedback_rate')
        ? "That's enough reports for this hour."
        : 'The report did not send. Try again in a moment.';
    } finally {
      this.feedbackSending = false;
      this.emit();
    }
  }

  async signOutAccount(): Promise<void> {
    await this.flushSpend();
    this.dropPendingSpend();
    await this.backend.signOutAccount?.();
    const loaded = this.backend.load();
    if (loaded) this.file = loaded;
    this.account = this.backend.account();
    this.fight = null;
    this.modal = null;
    this.emit();
  }

  async playHere(): Promise<void> {
    await this.flushSpend();
    this.dropPendingSpend();
    await this.backend.playHere?.();
    const loaded = this.backend.load();
    if (loaded) this.file = loaded;
    this.account = this.backend.account();
    this.modal = this.backend.consumeOfflineSummary?.() ?? this.modal;
    this.emit();
  }

  resetSave(): void {
    if (this.account.readOnly) return;
    this.dropPendingSpend();
    this.clearInFlight();
    if (this.backend.offlineClaims === 'server') {
      this.file.data = createNewFile().data;
      this.fight = null;
      this.lastLog = [];
      this.lastResult = null;
      this.modal = null;
      this.nextFightAt = 0;
      this.persist();
      this.emit();
      return;
    }
    this.backend.clear();
    this.file = this.backend.commit(0, createNewFile().data);
    this.fight = null;
    this.lastLog = [];
    this.lastResult = null;
    this.modal = null;
    this.nextFightAt = 0;
    this.emit();
  }

  get save(): SaveFile {
    return this.file;
  }

  private claimReturn(explicitPreview: boolean): void {
    let nextId = this.file.data.gear.nextItemId;
    const farmId = this.file.data.idle.snapshot?.farmStageId ?? this.file.data.progress.farmStage;
    const seed = this.file.data.idle.snapshot?.seed ?? 1;
    const result = claimIfDue({
      data: this.file.data,
      serverNowMs: this.backend.serverNow(),
      explicitPreview,
      rollItems: (count, pity) => {
        const tier = farmId > 0 ? getStage(farmId).tier : 1;
        const rolled = rollFarmDrops({ count, tier, pity, seed, startingId: nextId });
        nextId = rolled.nextId;
        return { items: rolled.items, pity: rolled.pity };
      },
    });
    const changed =
      result.summary !== null ||
      result.data.idle.pendingClaim !== this.file.data.idle.pendingClaim ||
      result.data.currencies.gold !== this.file.data.currencies.gold;
    if (!changed && !explicitPreview) return;
    result.data.gear.nextItemId = Math.max(result.data.gear.nextItemId, nextId);
    this.file.data = result.data;
    this.persist();
    if (explicitPreview) {
      this.modal = result.summary;
    } else if (result.summary && result.summary.gains.elapsedMs >= 60_000) {
      const gained =
        result.summary.goldGained +
        result.summary.xpGained +
        result.summary.dustGained +
        result.summary.keptItems.length +
        result.summary.salvaged;
      if (gained > 0 || result.summary.gains.capped) this.modal = result.summary;
    }
  }

  private applyFight(result: CombatResult): void {
    if (this.backend.offlineClaims === 'server') {
      this.lastLog = result.log;
      this.lastResult = result;
      this.patchCombatReadout();
      if (
        result.outcome === 'victory' &&
        this.backend.settleFarmBatch &&
        isRepeatFarmStage(this.file.data.progress, result.stageId)
      ) {
        this.queueFarm(result.stageId);
        this.emit('status');
        return;
      }
      void this.submitClear(result.stageId);
      return;
    }
    const data = this.file.data;
    const stage = getStage(result.stageId);
    const beforeLevel = data.hero.level;
    data.currencies.gold += result.gold;
    data.currencies.forgeDust += result.dust;
    const farmId = data.progress.farmStage;
    const leveled = grantXp(
      data.hero.level,
      data.hero.xp,
      data.hero.unspentPoints,
      result.outcome === 'victory'
        ? awardedClearXp({
            stageId: stage.id,
            isWall: stage.isWall,
            clearXp: result.xp,
            heroLevel: data.hero.level,
            bestCleared: data.progress.bestCleared,
            farmStageId: farmId,
            farmClearXp: farmId >= 1 ? getStage(farmId).clearXp : 0,
          })
        : 0,
    );
    data.hero.level = leveled.level;
    data.hero.xp = leveled.xp;
    data.hero.unspentPoints = leveled.unspent;
    if (result.dropRolls > 0) {
      const rolled = rollFarmDrops({
        count: result.dropRolls,
        tier: stage.tier,
        pity: data.gear.pity,
        seed: this.dropSeed,
        startingId: data.gear.nextItemId,
      });
      data.gear.nextItemId = rolled.nextId;
      data.gear.pity = rolled.pity;
      const bag = absorbDrops(data, rolled.items);
      data.gear.bag = bag.bag;
      data.currencies.gold += bag.gold;
      data.currencies.forgeDust += bag.forgeDust;
    }
    const beforeFarm = data.progress.farmStage;
    const beforeView = `${data.progress.mode}:${data.progress.drillStage}:${stageToFight(data.progress)}`;
    data.progress = applyFightResult(data.progress, stage, result.outcome === 'victory', LAST_STAGE_ID);
    if (leveled.levelsGained > 0) {
      const points = `Level ${data.hero.level}. ${leveled.levelsGained} sub-spec point${leveled.levelsGained === 1 ? '' : 's'} to spend.`;
      data.progress.notice = data.progress.notice ? `${points} ${data.progress.notice}` : points;
    }
    this.lastLog = result.log;
    this.lastResult = result;
    if (data.progress.farmStage !== beforeFarm || data.hero.level !== beforeLevel) this.refreshSnapshot();
    this.persist();
    const afterView = `${data.progress.mode}:${data.progress.drillStage}:${stageToFight(data.progress)}`;
    if (this.tab === 'combat' && beforeView !== afterView) this.emit('all');
    else {
      this.emit('status');
      this.patchCombatReadout();
    }
  }

  private refreshSnapshot(): void {
    const data = this.file.data;
    const farm = data.progress.farmStage;
    if (farm <= 0) {
      data.idle.snapshot = null;
      return;
    }
    const stage = getStage(farm);
    const rates = benchmarkRates({ hero: this.resolved(), stage, seed: mixSeed(0xc0ffee, farm) });
    data.idle.snapshot = {
      farmStageId: farm,
      rates,
      capHours: earnedOfflineCapHours(),
      seed: mixSeed(0xc0ffee, farm),
    };
  }

  private dispatchGear(request: GearRequest, build: boolean, toast?: string): void {
    if (this.account.readOnly) return;
    if (this.backend.offlineClaims === 'server') {
      void this.adoptServer(async () => {
        const file = await this.backend.gearAction?.(request);
        if (file && toast) this.armToast(toast);
        return file;
      });
      return;
    }
    try {
      this.file.data = applyGearAction(this.file.data, request);
    } catch (error) {
      this.failGuest(error);
      return;
    }
    if (toast) this.armToast(toast);
    if (build) this.afterBuildChange();
    else {
      this.persist();
      this.emit();
    }
  }

  private armToast(text: string): void {
    this.recycleToast = text;
    if (typeof window === 'undefined') return;
    window.clearTimeout(this.toastTimer);
    this.toastTimer = window.setTimeout(() => {
      this.recycleToast = null;
      this.toastTimer = 0;
      this.emit();
    }, 2800);
  }

  private pointsToSpend(slot: SlotId, raw: number | 'max'): number {
    const spec = this.file.data.hero.build[slot];
    if (!spec) return 0;
    const rank = this.file.data.hero.build.ranks[spec] ?? 0;
    const room = SLOT_MAX_RANK[slot] - rank;
    if (room < 1 || this.file.data.hero.unspentPoints < 1) return 0;
    const asked = raw === 'max' ? room : raw;
    if (!Number.isInteger(asked) || asked < 1) return 0;
    return Math.min(asked, room, this.file.data.hero.unspentPoints);
  }

  /**
   * Adopt a server save and keep unconfirmed spends on top.
   * In-flight ranks are applied first, skipping any the server save already has.
   * If the remainder does not fit, they are treated as already in the save.
   * A queued count that does not fit is trimmed. Returns true when any queued rank was cut.
   */
  private adoptServerFile(file: SaveFile): boolean {
    this.file = file;
    if (this.spendPending.size === 0 && this.spendInFlight.size === 0) {
      this.spendBase = null;
      this.spendFlightBase = null;
      return false;
    }
    const baseline = this.spendFlightBase ?? this.spendBase;
    let next = file.data;
    try {
      for (const [slot, count] of this.spendInFlight) {
        const owed = stillOwed(ranksGained(baseline, file.data, slot), 0, count);
        if (owed > 0) next = spendRanks(next, [{ slot, count: owed }]);
      }
    } catch {
      next = file.data;
    }
    if (this.spendPending.size > 0) {
      this.spendBase = withoutCoveredPending(next, baseline, file.data, this.spendInFlight, this.spendPending);
    }
    let dropped = false;
    for (const [slot, count] of [...this.spendPending]) {
      const owed = stillOwed(ranksGained(baseline, file.data, slot), this.spendInFlight.get(slot) ?? 0, count);
      const covered = count - owed;
      const spec = next.hero.build[slot];
      const room = spec ? SLOT_MAX_RANK[slot] - (next.hero.build.ranks[spec] ?? 0) : 0;
      const fit = Math.max(0, Math.min(owed, room, next.hero.unspentPoints));
      if (fit < owed) dropped = true;
      const keep = covered + fit;
      if (keep === 0) {
        this.spendPending.delete(slot);
        continue;
      }
      if (keep !== count) this.spendPending.set(slot, keep);
      if (fit > 0) next = spendRanks(next, [{ slot, count: fit }]);
    }
    if (this.spendPending.size === 0) this.dropPendingSpend();
    this.file = { ...file, data: next };
    if (dropped) this.account = { ...this.account, notice: SPEND_REJECTED };
    return dropped;
  }

  /** Keep a trim notice when the caller refreshes account from the backend. */
  private refreshAccount(trimmed: boolean): void {
    const next = this.backend.account();
    this.account = trimmed ? { ...next, notice: SPEND_REJECTED } : next;
  }

  private dropPendingSpend(): void {
    this.spendPending.clear();
    this.spendBase = null;
    if (this.spendTimer !== null) {
      clearTimeout(this.spendTimer);
      this.spendTimer = null;
    }
    this.clearSpendRejection();
  }

  private clearInFlight(): void {
    this.spendInFlight.clear();
    this.spendFlightBase = null;
  }

  /** A later successful spend or a clean queue reset drops only this banner. */
  private clearSpendRejection(): void {
    if (this.account.notice !== SPEND_REJECTED) return;
    this.account = { ...this.account, notice: null };
  }

  private armSpendTimer(): void {
    if (this.spendTimer !== null) clearTimeout(this.spendTimer);
    this.spendTimer = setTimeout(() => {
      this.spendTimer = null;
      void this.flushSpend();
    }, SPEND_BATCH_MS);
  }

  private afterSpendSettled(run: () => void): void {
    if (this.spendPending.size === 0 && this.spendFlight === null) {
      run();
      return;
    }
    void this.flushSpend().then(run);
  }

  private async sendSpend(
    ranks: { slot: SlotId; count: number }[],
    base: SaveData,
    options?: { keepalive?: boolean },
  ): Promise<void> {
    const optimistic = structuredClone(this.file.data);
    try {
      const file = await this.backend.applySpend?.({ action: 'spend', ranks }, options);
      this.clearInFlight();
      if (!file) throw new Error('save_invalid');
      if (!spendLanded(optimistic, file.data, ranks)) {
        this.dropPendingSpend();
        this.file = file;
        this.account = { ...this.backend.account(), notice: SPEND_REJECTED };
        this.restartFight();
        this.emit();
        return;
      }
      this.refreshAccount(this.adoptServerFile(file));
      this.restartFight();
      this.emit();
    } catch (error) {
      this.clearInFlight();
      this.dropPendingSpend();
      const loaded = this.backend.load();
      this.file = loaded ?? { ...this.file, data: structuredClone(base) };
      const message = error instanceof Error ? error.message : '';
      const superseded = message.includes('superseded');
      this.account = {
        ...this.backend.account(),
        notice: superseded ? 'Signed in on another device. This session is read-only.' : SPEND_REJECTED,
        readOnly: superseded ? true : this.backend.account().readOnly,
      };
      if (superseded) this.fight = null;
      this.restartFight();
      this.emit();
    }
  }

  private afterBuildChange(): void {
    this.refreshSnapshot();
    this.restartFight();
    this.persist();
    if (this.backend.offlineClaims === 'server') {
      void this.backend
        .flush?.()
        .then(async () => {
          await this.backend.commitLoadout?.();
          const loaded = this.backend.load();
          if (loaded) this.refreshAccount(this.adoptServerFile(loaded));
          else this.account = this.backend.account();
          this.emit();
        })
        .catch(() => undefined);
    }
    this.emit();
  }

  private adoptFrontier(request: FrontierRequest): Promise<void> {
    return this.adoptServer(() => this.backend.applyFrontier?.(request));
  }

  private async adoptServer(work: () => Promise<SaveFile | undefined> | undefined): Promise<void> {
    try {
      const file = await work();
      if (!file) throw new Error('save_invalid');
      this.refreshAccount(this.adoptServerFile(file));
      this.restartFight();
      this.emit();
    } catch (error) {
      this.account = {
        ...this.backend.account(),
        notice: this.serverNotice(error),
      };
      const message = error instanceof Error ? error.message : '';
      if (message.includes('superseded')) {
        this.account = { ...this.account, readOnly: true };
        this.fight = null;
      }
      const loaded = this.backend.load();
      if (loaded) this.adoptServerFile(loaded);
      this.emit();
    }
  }

  clearHoldText(): string {
    if (this.queuedClearStage !== null && !this.breathing) return 'server busy, tap to retry';
    if (this.breathing) return 'catching breath';
    return '';
  }

  /** One more submit of the queued clear. Auto-retries stop again after three. */
  retryQueuedClear(): void {
    if (this.queuedClearStage === null || this.serverBusy || this.account.readOnly) return;
    void this.submitClear(this.queuedClearStage);
  }

  private async submitClear(stageId: number): Promise<void> {
    this.serverBusy = true;
    let autoRetries = 0;
    try {
      const queuedSeq = this.farmQueue?.seq ?? null;
      await this.flushFarm();
      const pending = this.farmQueue;
      if (pending && pending.count >= 1 && pending.seq === queuedSeq) {
        this.breathing = false;
        this.queuedClearStage = stageId;
        this.account = { ...this.backend.account(), notice: null };
        this.patchCombatReadout();
        return;
      }
      for (;;) {
        try {
          const file = await this.backend.validateClear?.(stageId);
          if (!file) throw new Error('save_invalid');
          this.refreshAccount(this.adoptServerFile(file));
          this.breathing = false;
          this.queuedClearStage = null;
          this.restartFight();
          this.emit();
          return;
        } catch (error) {
          if (!isTooFast(error)) {
            this.breathing = false;
            this.queuedClearStage = null;
            this.failServer(error);
            return;
          }
          const wait = clearRetryWaitMs(retryAfterMs(error));
          if (autoRetries >= CLEAR_AUTO_RETRIES) {
            this.breathing = false;
            this.queuedClearStage = stageId;
            this.account = { ...this.backend.account(), notice: null };
            this.patchCombatReadout();
            return;
          }
          autoRetries += 1;
          this.breathing = true;
          this.queuedClearStage = null;
          this.account = { ...this.backend.account(), notice: null };
          this.patchCombatReadout();
          await waitMs(wait);
        }
      }
    } finally {
      this.serverBusy = false;
      this.breathing = false;
    }
  }

  private serverNotice(error: unknown): string {
    if (error instanceof RemoteError && (error.code === 'batch_stale' || error.code === 'batch_gap' || error.code === 'batch_stage')) {
      return error.message;
    }
    const message = error instanceof Error ? error.message : '';
    if (message.includes('superseded')) return 'Signed in on another device. This session is read-only.';
    if (message.includes('batch_stale')) return FUNCTION_ERROR_MESSAGES.batch_stale;
    if (message.includes('batch_gap')) return FUNCTION_ERROR_MESSAGES.batch_gap;
    if (message.includes('batch_stage')) return FUNCTION_ERROR_MESSAGES.batch_stage;
    return this.backend.account().notice ?? 'The server did not accept that change.';
  }

  private failServer(error: unknown): void {
    const message = error instanceof Error ? error.message : '';
    this.account = {
      ...this.backend.account(),
      notice: this.serverNotice(error),
    };
    if (message.includes('superseded')) {
      this.account = { ...this.account, readOnly: true };
      this.fight = null;
    }
    const loaded = this.backend.load();
    if (loaded) this.adoptServerFile(loaded);
    this.emit();
  }

  private restartFight(): void {
    this.fight = null;
    this.nextFightAt = 0;
  }

  private rememberQueue(queue: FarmQueue | null): void {
    this.farmQueue = queue && queue.count >= 1 ? queue : null;
    this.backend.writeFarmQueue?.(this.farmQueue);
  }

  private queueFarm(stageId: number): void {
    const now = Date.now();
    const current = this.farmQueue;
    if (current && current.count >= 1 && current.stageId !== stageId) {
      this.pendingFarmStage = stageId;
      void this.flushFarm().then(() => this.takePendingFarm());
      return;
    }
    const seq = current && current.count >= 1 ? current.seq : (this.file.data.idle.farmBatch?.seq ?? 0) + 1;
    const next = enqueueFarmClear(current, stageId, seq, now);
    this.rememberQueue(next);
    if (now - next.startedAtMs >= FARM_BATCH_INTERVAL_MS) void this.flushFarm();
  }

  private takePendingFarm(): void {
    const stageId = this.pendingFarmStage;
    if (stageId === null) return;
    if (this.farmQueue && this.farmQueue.count >= 1) return;
    this.pendingFarmStage = null;
    const seq = (this.file.data.idle.farmBatch?.seq ?? 0) + 1;
    this.rememberQueue(enqueueFarmClear(null, stageId, seq, Date.now()));
  }

  /**
   * One validate_clear for the queued repeat-farm wins.
   * keepalive lets the browser finish the request as the tab closes. The bearer token
   * has to be on the request, so this is fetch keepalive rather than sendBeacon.
   */
  private flushFarm(options?: { keepalive?: boolean }): Promise<void> {
    if (!this.backend.settleFarmBatch) return Promise.resolve();
    const sending = this.farmQueue;
    if (!sending || sending.count < 1) return this.farmFlush;
    if (this.farmSending) return this.farmFlush;
    this.farmSending = true;
    const settle = this.backend.settleFarmBatch(
      { stageId: sending.stageId, count: sending.count, seq: sending.seq },
      options,
    );
    this.farmFlush = (async () => {
      try {
        const file = await settle;
        const receipt = file.data.idle.farmBatch?.receipt;
        this.rememberQueue(
          carryFarmQueue(
            this.farmQueue,
            sending,
            receipt ? { seq: receipt.seq, requested: receipt.requested } : null,
          ),
        );
        this.refreshAccount(this.adoptServerFile(file));
        this.restartFight();
        this.emit();
      } catch (error) {
        const message = error instanceof Error ? error.message : '';
        const code = error instanceof RemoteError ? error.code : message;
        if (
          code === 'save_invalid' ||
          code === 'batch_stale' ||
          code === 'batch_gap' ||
          code === 'batch_stage' ||
          message.includes('save_invalid') ||
          message.includes('batch_')
        ) {
          this.rememberQueue(null);
          this.failServer(error);
        }
      } finally {
        this.farmSending = false;
        this.takePendingFarm();
      }
    })();
    return this.farmFlush;
  }

  private resolved(): ResolvedHero {
    const data = this.file.data;
    return resolveHero({
      level: data.hero.level,
      build: data.hero.build,
      gear: data.gear.equipped,
      rules: data.hero.rules,
    });
  }

  private finishBoot(): void {
    this.account = this.backend.account();
    const loaded = this.backend.load();
    this.file = loaded ?? this.backend.commit(0, createNewFile().data);
    this.farmQueue = this.backend.readFarmQueue?.() ?? null;
    if (this.backend.offlineClaims === 'server') this.modal = this.backend.consumeOfflineSummary?.() ?? null;
    else this.claimReturn(false);
    this.readyResolve();
    this.emit();
  }

  private onBackend(event: BackendEvent): void {
    if (event.type === 'account') {
      this.account = event.account;
      if (event.account.readOnly) this.fight = null;
      this.emit();
      return;
    }
    this.refreshAccount(this.adoptServerFile(event.file));
    this.fight = null;
    if (event.reason === 'save_conflict' && this.account.notice) {
      this.file.data.progress.notice = this.account.notice;
    }
    this.emit();
  }

  /** Write the save after a hide spend. Ranks still sitting in the queue stay off disk. */
  private persistConfirmedSpend(): void {
    if (this.spendPending.size > 0) {
      if (!this.spendBase) return;
      const shown = this.file.data;
      this.file = { ...this.file, data: structuredClone(this.spendBase) };
      this.refreshSnapshot();
      this.persist();
      this.file = { ...this.file, data: shown };
      return;
    }
    this.refreshSnapshot();
    this.persist();
  }

  private persist(): void {
    if (this.account.readOnly) return;
    try {
      const committed = this.backend.commit(this.file.version, this.file.data);
      if (this.backend.offlineClaims === 'server') this.adoptServerFile(committed);
      else this.file = committed;
      if (this.guestSaveFault && this.account.notice === this.guestSaveFault) {
        this.account = { ...this.account, notice: null };
      }
      this.guestSaveFault = null;
    } catch (error) {
      const message = error instanceof Error ? error.message : '';
      if (message.includes('superseded')) {
        this.account = { ...this.account, readOnly: true };
        this.fight = null;
      }
      this.failGuest(error);
    }
  }

  private failGuest(error: unknown): void {
    const code = error instanceof Error && error.message ? error.message : 'save_invalid';
    console.error('guest save failed', code);
    const notice = guestSaveMessage(code);
    this.guestSaveFault = notice;
    this.account = { ...this.account, notice };
    this.emit();
  }

  private patchLog(): void {
    if (this.feedbackOpen || typeof document === 'undefined') return;
    const node = document.getElementById('fight-log');
    if (!node || !this.fight) return;
    node.textContent = this.fight.log.slice(-8).join('\n');
  }

  private patchCombatReadout(): void {
    if (this.feedbackOpen) return;
    this.patchLog();
    if (typeof document === 'undefined') return;
    const notice = document.getElementById('combat-notice');
    if (notice) notice.textContent = this.file.data.progress.notice ?? '';
    const result = document.getElementById('combat-result');
    if (result && this.lastResult) {
      const outcome = this.lastResult.outcome === 'victory' ? 'Cleared' : 'Held';
      const hold = this.clearHoldText();
      result.textContent = `${outcome} in ${this.lastResult.seconds.toFixed(1)}s · +${this.lastResult.gold} gold${hold ? ` · ${hold}` : ''}`;
      result.classList.toggle('is-retry', hold === 'server busy, tap to retry');
    }
  }

  private emit(scope: 'all' | 'status' = 'all'): void {
    for (const listener of this.listeners) listener(scope);
  }
}

/** How many ranks this slot gained between the pre-spend save and the server save. Null when that delta is not meaningful. */
function ranksGained(base: SaveData | null, server: SaveData, slot: SlotId): number | null {
  if (!base) return null;
  const spec = base.hero.build[slot];
  if (!spec || server.hero.build[slot] !== spec) return null;
  const before = base.hero.build.ranks[spec] ?? 0;
  const now = server.hero.build.ranks[spec] ?? 0;
  if (now < before) return null;
  return now - before;
}

/** Ranks from `count` that are not already in the server save after skipping earlier unconfirmed ranks. */
function stillOwed(gained: number | null, skip: number, count: number): number {
  if (gained === null) return count;
  const covered = Math.min(count, Math.max(0, gained - skip));
  return count - covered;
}

/** Rollback picture for the queue: in-flight ranks stay, pending ranks that are already visible do not. */
function withoutCoveredPending(
  painted: SaveData,
  baseline: SaveData | null,
  server: SaveData,
  inFlight: ReadonlyMap<SlotId, number>,
  pending: ReadonlyMap<SlotId, number>,
): SaveData {
  const base = structuredClone(painted);
  for (const [slot, count] of pending) {
    const covered = count - stillOwed(ranksGained(baseline, server, slot), inFlight.get(slot) ?? 0, count);
    if (covered < 1) continue;
    const spec = base.hero.build[slot];
    if (!spec) continue;
    base.hero.build.ranks[spec] = Math.max(0, (base.hero.build.ranks[spec] ?? 0) - covered);
    base.hero.unspentPoints += covered;
  }
  return base;
}

function spendLanded(
  optimistic: SaveData,
  returned: SaveData,
  ranks: readonly { slot: SlotId; count: number }[],
): boolean {
  for (const { slot } of ranks) {
    const spec = optimistic.hero.build[slot];
    if (!spec || returned.hero.build[slot] !== spec) return false;
    const want = optimistic.hero.build.ranks[spec] ?? 0;
    const got = returned.hero.build.ranks[spec] ?? 0;
    if (got < want) return false;
  }
  return true;
}

function guestSaveMessage(code: string): string {
  if (code.includes('superseded')) return 'Signed in on another device. This session is read-only.';
  if (code.includes('save_conflict')) return 'This save changed somewhere else.';
  return 'This browser could not save that change.';
}

function rewardLabel(preview: { gold: number; forgeDust: number }): string {
  if (preview.forgeDust > 0 && preview.gold > 0) return `+${preview.forgeDust} dust, +${preview.gold} gold`;
  if (preview.forgeDust > 0) return `+${preview.forgeDust} dust`;
  return `+${preview.gold} gold`;
}

function isTooFast(error: unknown): boolean {
  if (error instanceof RemoteError) return error.code === 'too_fast';
  return error instanceof Error && error.message.includes('too_fast');
}

function retryAfterMs(error: unknown): number | undefined {
  if (error instanceof RemoteError) return error.retryAfterMs;
  return undefined;
}

function waitMs(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

function nextRuleId(rules: Rule[]): string {
  const ids = new Set(rules.map((rule) => rule.id));
  let n = rules.length + 1;
  while (ids.has(`rule-${n}`)) n += 1;
  return `rule-${n}`;
}

function conditionFrom(kind: Condition['kind'], param: number | undefined, previous: Condition): Condition {
  if (kind === 'selfHpBelow') {
    const pct = param ?? (previous.kind === 'selfHpBelow' ? previous.pct : 50);
    return { kind, pct };
  }
  if (kind === 'enemyCountAtLeast') {
    const count = param ?? (previous.kind === 'enemyCountAtLeast' ? previous.count : 2);
    return { kind, count };
  }
  if (kind === 'always' || kind === 'targetIsEliteOrBoss' || kind === 'targetHasArmor') return { kind };
  return { kind: 'always' };
}
